import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { NavigationMenu } from '../typescript/response';

/**
 * Normalizes a page URL into a stable lookup key:
 * strips query/hash, removes a trailing slash, and lowercases.
 * Returns null for empty/missing urls or the site root.
 */
const normalizePath = (url?: string | null): string | null => {
  if (!url) return null;
  const clean = url.split(/[?#]/)[0].replace(/\/+$/, '').toLowerCase();
  if (!clean || clean === '/') return null;
  return clean.startsWith('/') ? clean : `/${clean}`;
};

/**
 * Builds a map of normalized page url -> localized label from the header
 * navigation menu (top-level items and their sub-navigation). The labels are
 * authored per-locale in Contentstack, so the resulting map is already
 * translated for whatever locale the header was fetched in.
 */
const buildLabelMap = (navMenu?: NavigationMenu[]): Record<string, string> => {
  const map: Record<string, string> = {};

  navMenu?.forEach((item) => {
    const url = normalizePath(item?.page_reference?.[0]?.url);
    if (url && item?.label) map[url] = item.label;

    item?.sub_navigation?.forEach((sub) => {
      const subUrl = normalizePath(sub?.page_reference?.[0]?.url);
      if (subUrl && sub?.label) map[subUrl + (sub?.querystring ? `?${sub?.querystring}` : '')] = sub.label;
    });
  });

  return map;
};

const BreadcrumbLabelsContext = createContext<Record<string, string>>({});

/** Returns the url -> localized label map (empty object outside a provider). */
export const useBreadcrumbLabels = () => useContext(BreadcrumbLabelsContext);

/** Looks up a localized label for a normalized cumulative path, or undefined. */
export const useBreadcrumbLabel = (path: string): string | undefined => {
  const labels = useBreadcrumbLabels();
  const key = normalizePath(path);
  return key ? labels[key] : undefined;
};

export const BreadcrumbLabelsProvider = ({
  navMenu,
  children,
}: {
  navMenu?: NavigationMenu[];
  children: ReactNode;
}) => {
  const value = useMemo(() => buildLabelMap(navMenu), [navMenu]);
  return (
    <BreadcrumbLabelsContext.Provider value={value}>
      {children}
    </BreadcrumbLabelsContext.Provider>
  );
};
